import AddIcon from "@mui/icons-material/Add";
import {
    Button,
    CardHeader,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { withStyles } from "@mui/styles";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface State {
    list: any[];
    loaded: boolean;
    generateInProgress: boolean;
    showModal: boolean;
    logs: any[];
    page: number;
    perPage: number;
    haveError: boolean;
}

interface BotsWrappedProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
    form: any;
    socket: any;
}

class BotsWrapped extends React.Component<BotsWrappedProps, State> {

    constructor(props) {
        super(props);

        this.state = {
            list: [],
            loaded: false,
            generateInProgress: false,
            showModal: false,
            logs: [],
            page: 0,
            perPage: 25,
            haveError: false,
        };

        document.title = Helpers.documentTitle("Ghost bases");
    }

    componentDidMount() {
        if (this.props.socket) {
            this.props.socket.on("GhostBaseUpdate", data => {
                if (data.exporterUserId === this.props.activeUser.userId) {
                    if (data.finished === true && !data.error) {
                        this.cancel();
                        this.fetchBots();
                    }

                    this.setState({
                        logs: [...this.state.logs, {
                            log: data.log,
                            date: new Date(),
                        }],
                        ...(data.log.indexOf("Error") > -1 && {
                            generateInProgress: false,
                            haveError: true,
                        })
                    });
                }
            });
        }

        this.fetchBots();
    }

    generateRows = () => {
        const rows = [];
        const { getFieldDecorator } = this.props.form;

        for (let i = 1; i <= 25; i++) {
            rows.push(<TableRow>
                <TableCell>
                    {i}
                </TableCell>
                <TableCell>
                    {getFieldDecorator(`hq_amount[${i}]`, {
                        initialValue: 0,
                    })(
                        <TextField variant="outlined"/>
                    )}
                </TableCell>
            </TableRow>);
        }

        return <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Core level</TableCell>
                    <TableCell>Amount of users</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows}
            </TableBody>
        </Table>;
    }

    fetchBots = async () => {
        try {
            // Put data to S3
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/bots`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });

            this.setState({ list: response.data.data, loaded: true });
        } catch (error) {
            this.setState({ loaded: true });
            PopupMessage.error("Couldn't fetch ghost bases");
        }
    }

    updateSourceFields = () => {
        axios.put(`${Config.getInstance.apiUrl}/api/bots/update_source_fields`, {},
        {
            headers: {
                Authorization: Helpers.getSessionId(),
            }
        })
        .then(response => {
            PopupMessage.success("Success");
        })
        .catch(_ => {
            PopupMessage.error("Something went wrong!");
        });
}

    generate = () => {
        this.props.form.validateFields((error, value) => {
            if (error) {
                return;
            }

            this.setState({ generateInProgress: true, haveError: false });

            // Group users amount per HQ level
            const userAmountPerHq = value.hq_amount.reduce((list, item, index) => {
                if (index > 0) {
                    list[index.toString()] = parseInt(item, 10);
                }

                return list;
            }, {});

            axios.post(`${Config.getInstance.apiUrl}/api/bots/generate`, {
                amount: userAmountPerHq,
                exporterUserId: this.props.activeUser.userId,
            },
            {
                headers: {
                    Authorization: Helpers.getSessionId(),
                }
            })
            .catch(_ => {
                this.setState({ generateInProgress: false, logs: [] });
                PopupMessage.error("Something went wrong!");
            });
        });
    }

    cancel = () => {
        this.setState({ showModal: false, generateInProgress: false, logs: [], haveError: false });
        this.props.form.resetFields();
    }

    showModal = () => {
        this.setState({ showModal: true });
    }

    handleChangePage = (_event: unknown, newPage: number) => {
        this.setState({ page: newPage });
    }

    handleChangeRowsPerPage = (event: any) => {
        this.setState({
            page: 0,
            perPage: parseInt(event.target.value, 10),
        });
    }

    renderTableContent() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={5}>
                    {Helpers.renderSkeletonRows(5, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.list.length === 0) {
            return <TableRow key="not_found">
                <TableCell colSpan={5}>
                    <Alert severity="warning">No ghost bases were found</Alert>
                </TableCell>
            </TableRow>
        }

        return this.state.list.slice(this.state.page * this.state.perPage, this.state.page * this.state.perPage + this.state.perPage)
            .map(base => (
                <TableRow key={base.id}>
                    <TableCell component="th" scope="row">
                        <NavLink to={`/players/${base.id}`}>
                            {base.id}
                        </NavLink>
                    </TableCell>
                    <TableCell align="left">{base.name}</TableCell>
                    <TableCell align="left">
                        {base.linked_account ? <NavLink to={`/players/${base.linked_account}`}>
                            {base.linked_account}
                        </NavLink> : "Not found"}
                    </TableCell>
                    <TableCell align="left">{base.headquarter_level}</TableCell>
                    <TableCell align="left">{base.rank}</TableCell>
                </TableRow>
            ));
    }

    renderAction() {
        return <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Button disableElevation color="primary" variant="contained" size="small" onClick={this.showModal}>
                <AddIcon/> Generate
            </Button>

            {this.state.loaded ? <Button
                disableElevation
                color="primary"
                variant="contained"
                size="small"
                onClick={this.updateSourceFields}
                style={{ marginLeft: 10 }}
            >
                Update source field
            </Button> : <Skeleton width={140} height={50} style={{ display: "inline-block", marginLeft: 10 }}/>}
        </div>
    }

    render() {
        const {classes} = this.props;

        return (
            <div className="contentPadding">
                <Paper elevation={0}>
                    <CardHeader
                        title={`Ghost bases ${this.state.loaded ? `(${this.state.list.length})` : ""}`}
                        action={this.renderAction()}
                    />

                    <Table aria-label="simple table" style={{ tableLayout: "inherit" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Id</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Copied from</TableCell>
                                <TableCell>Core level</TableCell>
                                <TableCell>Rank</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.renderTableContent()}
                        </TableBody>
                    </Table>

                    {this.state.loaded && this.state.list.length > 0 && <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={this.state.list.length}
                        rowsPerPage={this.state.perPage}
                        page={this.state.page}
                        onPageChange={this.handleChangePage}
                        onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />}
                </Paper>

                <Dialog
                    open={this.state.showModal}
                    onClose={this.cancel}
                    fullWidth={true}
                    maxWidth="sm"
                >
                    <DialogTitle>
                        Generate ghost bases
                    </DialogTitle>

                    <DialogContent dividers>
                        <Alert severity="warning">Ghost bases are generated based on existing users</Alert>

                        {(!this.state.generateInProgress && !this.state.haveError) && <React.Fragment>
                            {this.generateRows()}
                        </React.Fragment>}

                        {this.state.generateInProgress || this.state.haveError && <React.Fragment>
                            {!this.state.haveError && <LinearProgress />}

                            <div className="fakeScreen">
                                {this.state.logs.map(log => <p className="line" key={log.log + log.date}>
                                    <span style={{ paddingRight: 10 }}>
                                        [{Helpers.formatDateTime(log.date || new Date(), true)}]
                                    </span>

                                    {log.log}
                                </p>)}
                            </div>
                        </React.Fragment>}
                    </DialogContent>

                    <DialogActions>
                        <Button disableElevation
                            onClick={this.cancel}
                            disabled={this.state.generateInProgress}
                            variant="contained"
                        >
                            Cancel
                        </Button>

                        <div className={classes.wrapper}>
                            <Button disableElevation
                                onClick={this.generate}
                                color="primary"
                                variant="contained"
                                className={classes.loadingButton}
                                disabled={this.state.generateInProgress}
                            >
                                Generate
                            </Button>

                            {this.state.generateInProgress && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

const styles: any = theme => ({
    loadingButton: {
        width: "100%",
    },
    wrapper: {
        margin: 8,
        position: "relative",
        marginTop: 10,
        width: 150,
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
});

export const Bots = createForm()(withStyles(styles)(BotsWrapped));
